'use client';

import { CRAWLER_PERIODIC_WINDOW_HOURS } from '@terra/shared-utils';
import { Badge } from '../../../components/badge';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../../components/typography';
import {
  DAYS_OF_WEEK,
  WORKING_HOUR_OPTIONS,
  buildTimezoneSelectItems,
  formatHourLabel,
  getBrowserTimezone,
} from '../../automation/automationScheduleShared';
import { Select, createSelectListCollection } from '../../../components/select';
import { SettingsEditDialog } from '../SettingsEditDialog';
import { SettingsRow } from '../SettingsRow';
import { ToggleGroup } from '../../../components/toggle-group';
import { Tooltip } from '../../../components/tooltip';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, HelpCircleIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Fragment, useMemo, useState } from 'react';

type CrawlerWindowDetailsSectionProps = {
  selectedDay: number;
  selectedStartHour: number;
  selectedTimezone: string;
  disabled?: boolean;
  onDayChange: (dayOfWeek: number) => void;
  onStartHourChange: (startHour: number) => void;
  onTimezoneChange: (timezone: string) => void;
};

export function CrawlerWindowDetailsSection({
  selectedDay,
  selectedStartHour,
  selectedTimezone,
  disabled,
  onDayChange,
  onStartHourChange,
  onTimezoneChange,
}: CrawlerWindowDetailsSectionProps) {
  const t = useTranslations('designSystem.automation.crawler');
  const tSchedule = useTranslations('designSystem.automation.schedule');
  const tCommon = useTranslations('designSystem.common');
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [isTimeRangeDialogOpen, setIsTimeRangeDialogOpen] = useState(false);
  const [timezoneDraft, setTimezoneDraft] = useState(selectedTimezone);
  const [startHourDraft, setStartHourDraft] = useState(selectedStartHour);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);

  const selectedEndHour =
    (selectedStartHour + CRAWLER_PERIODIC_WINDOW_HOURS) % 24;
  const dayLabel =
    DAYS_OF_WEEK.find((day) => day.value === selectedDay)?.label ??
    String(selectedDay);

  const timezoneItems = useMemo(
    () =>
      buildTimezoneSelectItems({
        browserTimezone,
        selectedTimezone,
      }),
    [browserTimezone, selectedTimezone],
  );
  const timezoneCollection = useMemo(
    () => createSelectListCollection({ items: timezoneItems }),
    [timezoneItems],
  );
  const selectedTimezoneLabel =
    timezoneItems.find((item) => item.value === selectedTimezone)?.label ??
    selectedTimezone;

  const startHourItems = useMemo(
    () =>
      WORKING_HOUR_OPTIONS.filter((hour) => hour < 24).map((hour) => ({
        label: t('window.timeRangeValue', {
          start: formatHourLabel(hour),
          end: formatHourLabel((hour + CRAWLER_PERIODIC_WINDOW_HOURS) % 24),
        }),
        value: String(hour),
      })),
    [t],
  );
  const startHourCollection = useMemo(
    () => createSelectListCollection({ items: startHourItems }),
    [startHourItems],
  );

  const windowSummary = t('window.nextCrawlWindowSummary', {
    day: dayLabel,
    start: formatHourLabel(selectedStartHour),
    end: formatHourLabel(selectedEndHour),
  });

  const detailRows = [
    <SettingsRow
      key="day"
      label={t('window.dayOfWeek')}
      tooltip={t('window.dayOfWeekTooltip')}
      control={
        <ToggleGroup.Root
          value={[String(selectedDay)]}
          onValueChange={({ value: nextValues }) => {
            const nextDay = Number.parseInt(nextValues[0] ?? '', 10);

            if (Number.isFinite(nextDay)) {
              onDayChange(nextDay);
            }
          }}
          disabled={disabled}
          size="sm"
          variant="outline"
          gap={0}
          p={0}
          borderWidth={0}
          bg="transparent"
          _horizontal={{ divideX: '0' }}
        >
          {DAYS_OF_WEEK.map((day) => (
            <ToggleGroup.Item
              key={day.value}
              value={String(day.value)}
              aria-label={tSchedule(
                `days.${day.value}` as `days.${0 | 1 | 2 | 3 | 4 | 5 | 6}`,
              )}
              w="8"
              minW="8"
              h="8"
              px={0}
              borderWidth="1px"
              borderStyle="solid"
              borderColor="border.neutral.default"
              borderRadius="0"
              fontSize="xs"
              fontWeight="medium"
              color="fg.default"
              bg="bg.default"
              marginInlineStart="-1px"
              _first={{
                marginInlineStart: 0,
                borderTopLeftRadius: 'md',
                borderBottomLeftRadius: 'md',
              }}
              _last={{
                borderTopRightRadius: 'md',
                borderBottomRightRadius: 'md',
              }}
              _hover={{
                bg: 'bg.default',
                color: 'fg.default',
              }}
              _on={{
                borderColor: 'border.accent.default',
                bg: 'bg.accent.light',
                color: 'fg.accent.default',
                zIndex: 1,
                _hover: {
                  bg: 'bg.accent.light',
                  color: 'fg.accent.default',
                },
              }}
            >
              {day.short}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      }
      isReadOnly={disabled}
    />,
    <SettingsRow
      key="timezone"
      label={tSchedule('timezone')}
      value={selectedTimezoneLabel}
      action={{
        label: tCommon('edit'),
        onClick: () => {
          setTimezoneDraft(selectedTimezone);
          setIsTimezoneDialogOpen(true);
        },
        disabled,
        size: 'xs',
      }}
      isReadOnly={disabled}
    />,
    <SettingsRow
      key="time-range"
      label={
        <HStack gap={2} alignItems="center">
          <HStack gap={1} alignItems="center">
            <MetaDefault color="fg.neutral.muted">
              {t('window.timeRange')}
            </MetaDefault>
            <Tooltip
              title={t('window.timeRangeTooltip')}
              positioning={{ strategy: 'fixed' }}
            >
              <Box display="flex" color="fg.neutral.muted">
                <HelpCircleIcon size={16} />
              </Box>
            </Tooltip>
          </HStack>
          <Badge variant="outline" size="sm">
            {t('window.sixHourWindow')}
          </Badge>
        </HStack>
      }
      value={t('window.timeRangeValue', {
        start: formatHourLabel(selectedStartHour),
        end: formatHourLabel(selectedEndHour),
      })}
      action={{
        label: tCommon('edit'),
        onClick: () => {
          setStartHourDraft(selectedStartHour);
          setIsTimeRangeDialogOpen(true);
        },
        disabled,
        size: 'xs',
      }}
      isReadOnly={disabled}
    />,
  ];

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      <BodyStrong color="fg.neutral.default">
        {t('window.detailsTitle')}
      </BodyStrong>

      <Box
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border.neutral.default"
        borderRadius="md"
        bg="bg.neutral.default"
        px={3}
        py={1}
        w="full"
      >
        <VStack alignItems="stretch" gap={0} w="full">
          {detailRows.map((row, index) => (
            <Fragment key={row.key}>
              <Box py={3}>{row}</Box>
              {index < detailRows.length - 1 ? (
                <Box h="1px" w="full" bg="border.neutral.default" />
              ) : null}
            </Fragment>
          ))}
        </VStack>
      </Box>

      <BodyDefault color="fg.neutral.muted">
        {t('window.nextCrawlLead')}{' '}
        <BodyStrong as="span" color="fg.neutral.default">
          {windowSummary}
        </BodyStrong>
        {t('window.nextCrawlTrail', { timezone: selectedTimezone })}
      </BodyDefault>

      <SettingsEditDialog
        open={isTimezoneDialogOpen}
        title={tSchedule('timezone')}
        onOpenChange={setIsTimezoneDialogOpen}
        onSubmit={() => {
          onTimezoneChange(timezoneDraft);
          setIsTimezoneDialogOpen(false);
        }}
        isSubmitDisabled={timezoneDraft === selectedTimezone}
        submitText={tCommon('saveChanges')}
      >
        <Box w="full">
          <Select.Root
            collection={timezoneCollection}
            positioning={{ sameWidth: true }}
            value={[timezoneDraft]}
            onValueChange={(details) => {
              const value = details.value[0];

              if (value) {
                setTimezoneDraft(value);
              }
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={tSchedule('selectTimezone')} />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {timezoneItems.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>

      <SettingsEditDialog
        open={isTimeRangeDialogOpen}
        title={t('window.timeRange')}
        onOpenChange={setIsTimeRangeDialogOpen}
        onSubmit={() => {
          onStartHourChange(startHourDraft);
          setIsTimeRangeDialogOpen(false);
        }}
        isSubmitDisabled={startHourDraft === selectedStartHour}
        submitText={tCommon('saveChanges')}
      >
        <Box w="full">
          <Select.Root
            collection={startHourCollection}
            positioning={{ sameWidth: true }}
            value={[String(startHourDraft)]}
            onValueChange={(details) => {
              const value = details.value[0];

              if (!value) {
                return;
              }

              setStartHourDraft(Number.parseInt(value, 10));
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {startHourItems.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>
    </VStack>
  );
}
